<template>
  <NavBar pageName="Settings Parameters"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Settings Parameters</span>
        <v-btn color="success" size="small" @click="openAddDialog">
          <v-icon class="mr-1">mdi-plus</v-icon>
          Add
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search by field name"
          variant="outlined"
          density="compact"
          clearable
          class="mb-3"
          style="max-width: 400px;"
        ></v-text-field>

        <v-data-table
          :headers="headers"
          :items="filteredSettings"
          :loading="loading"
          :items-per-page="20"
          class="elevation-1"
        >
          <template v-slot:[`item.field_type`]="{ item }">
            <v-chip size="small" :color="fieldTypeColor(item.field_type)">
              {{ item.field_type }}
            </v-chip>
          </template>

          <template v-slot:[`item.value`]="{ item }">
            <span class="font-weight-medium">{{ item.value }}</span>
          </template>

          <template v-slot:[`item.updated_at`]="{ item }">
            {{ formatDateTime(item.updated_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon size="small" variant="text" color="primary" @click="openEditDialog(item)" title="Edit">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon size="small" variant="text" color="error" @click="openDeleteDialog(item)" title="Delete">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? 'Edit Setting' : 'Add Setting' }}
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="formData.field_name"
            label="Field Name"
            variant="outlined"
            density="compact"
            :rules="[v => !!v || 'Field name is required']"
            class="mb-3"
            placeholder="e.g. MAX_RECHARGE_AMOUNT"
          ></v-text-field>

          <v-select
            v-model="formData.field_type"
            :items="fieldTypes"
            label="Field Type"
            variant="outlined"
            density="compact"
            class="mb-3"
          ></v-select>

          <v-text-field
            v-model="formData.value"
            label="Value"
            variant="outlined"
            density="compact"
            :rules="[v => v !== '' && v !== null && v !== undefined || 'Value is required']"
            class="mb-3"
          ></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveSetting"
            :loading="saving"
            :disabled="!formData.field_name || formData.value === '' || formData.value === null || formData.value === undefined"
          >
            {{ isEditing ? 'Update' : 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Delete Setting</v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ deletingItem?.field_name }}</strong>?
          <br><br>
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import { getSettingsParameters, saveSettingParameter, deleteSettingParameter } from "@/composables/useSettingsParametersAPI";

export default defineComponent({
  name: 'SettingsParametersView',
  components: {
    NavBar
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      settings: [],
      search: '',
      showDialog: false,
      showDeleteDialog: false,
      isEditing: false,
      editingId: null,
      deletingItem: null,
      formData: {
        field_name: '',
        field_type: 'string',
        value: ''
      },
      fieldTypes: ['string', 'integer', 'float', 'boolean'],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    token() {
      return store.getters.getToken;
    },
    headers() {
      return [
        { title: 'ID', key: 'id', width: '60px' },
        { title: 'Field Name', key: 'field_name' },
        { title: 'Type', key: 'field_type', width: '100px' },
        { title: 'Value', key: 'value', width: '200px' },
        { title: 'Updated', key: 'updated_at', width: '150px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
      ];
    },
    filteredSettings() {
      if (!this.search) return this.settings;
      const s = this.search.toLowerCase();
      return this.settings.filter(item =>
        item.field_name?.toLowerCase().includes(s) ||
        item.value?.toLowerCase().includes(s)
      );
    }
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await getSettingsParameters(this.token);
        if (!response.error) {
          this.settings = response.settings || [];
        } else {
          this.showMessage(response.message || 'Error loading settings', 'error');
        }
      } catch (err) {
        log("Error loading settings:", err);
        this.showMessage('Error loading settings', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.isEditing = false;
      this.editingId = null;
      this.formData = { field_name: '', field_type: 'string', value: '' };
      this.showDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.formData = {
        field_name: item.field_name,
        field_type: item.field_type || 'string',
        value: item.value
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
    },

    async saveSetting() {
      this.saving = true;
      try {
        const data = { ...this.formData };
        if (this.isEditing) {
          data.id = this.editingId;
        }
        const response = await saveSettingParameter(this.token, data);
        if (!response.error) {
          this.showMessage(response.message || 'Setting saved', 'success');
          this.closeDialog();
          await this.loadSettings();
        } else {
          this.showMessage(response.message || 'Error saving setting', 'error');
        }
      } catch (err) {
        log("Error saving setting:", err);
        this.showMessage('Error saving setting', 'error');
      } finally {
        this.saving = false;
      }
    },

    openDeleteDialog(item) {
      this.deletingItem = item;
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        const response = await deleteSettingParameter(this.token, this.deletingItem.id);
        if (!response.error) {
          this.showMessage(response.message || 'Setting deleted', 'success');
          this.showDeleteDialog = false;
          await this.loadSettings();
        } else {
          this.showMessage(response.message || 'Error deleting setting', 'error');
        }
      } catch (err) {
        log("Error deleting setting:", err);
        this.showMessage('Error deleting setting', 'error');
      } finally {
        this.deleting = false;
      }
    },

    fieldTypeColor(type) {
      const colors = { string: 'blue', integer: 'green', float: 'orange', boolean: 'purple' };
      return colors[type] || 'grey';
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadSettings();
  }
});
</script>
